import { inject, type InjectionKey } from 'vue'

/**
 * Tracker du funnel widget. Émet un CustomEvent `fg:event` sur l'élément hôte
 * `<ferrygate-booking>` à chaque jalon du tunnel de réservation, pour que la
 * page intégratrice puisse brancher son analytics / conversion (Google Ads,
 * PostHog, GTM…) sans rien connaître de l'intérieur du shadow DOM.
 *
 * Contrat de l'événement (stable, à documenter côté intégrateur) :
 *
 *   element.addEventListener('fg:event', (e) => {
 *     e.detail.step  // 'search_submitted' | 'results_viewed' | 'sailing_selected' | 'options_viewed' |
 *                    // 'passengers_started' | 'price_shown' | 'continue_clicked' | 'option_created' |
 *                    // 'checkout_redirect' | 'widget_error'
 *     e.detail.*     // métadonnées du jalon (route, count, bookingId, value, currency…)
 *   })
 *
 * `bubbles` + `composed` sont indispensables : sans eux l'événement reste
 * piégé dans le shadow DOM et n'atteint jamais document/window.
 *
 * Non-bloquant par conception : une erreur de tracking ne doit JAMAIS casser le
 * tunnel → tout est encapsulé dans un try/catch silencieux, et l'absence
 * d'élément hôte (tests, SSR) dégrade en no-op.
 */
export type FunnelStep =
  | 'search_submitted'
  | 'results_viewed'
  | 'sailing_selected'
  | 'options_viewed'
  | 'passengers_started'
  | 'price_shown'
  | 'continue_clicked'
  | 'option_created'
  | 'checkout_redirect'
  | 'widget_error'

export interface Tracker {
  emit(step: FunnelStep, meta?: Record<string, unknown>): void
  emitOnce(step: FunnelStep, meta?: Record<string, unknown>, key?: string): void
  reset(): void
}

export const TRACKER_KEY: InjectionKey<Tracker> = Symbol('fg-widget-tracker')

const noopTracker: Tracker = { emit() {}, emitOnce() {}, reset() {} }

export function createTracker(host: HTMLElement | null): Tracker {
  const emittedKeys = new Set<string>()

  function emit(step: FunnelStep, meta: Record<string, unknown> = {}): void {
    if (!host) return
    try {
      host.dispatchEvent(
        new CustomEvent('fg:event', {
          detail: { step, ...meta },
          bubbles: true,
          composed: true,
        }),
      )
    } catch {
      // Le tracking ne doit jamais casser le tunnel de réservation.
    }
  }

  return {
    emit,
    // Jalons « une fois par parcours » (ex. écrans montés/démontés en navigation
    // arrière) : dédup par `key` (défaut = step). reset() au démarrage d'une
    // nouvelle recherche réarme tout.
    emitOnce(step, meta = {}, key = step) {
      if (emittedKeys.has(key)) return
      emittedKeys.add(key)
      emit(step, meta)
    },
    reset() {
      emittedKeys.clear()
    },
  }
}

/** Injecte le tracker (no-op si non fourni, ex. composant testé isolément). */
export function useTracker(): Tracker {
  return inject(TRACKER_KEY, noopTracker)
}
